import MainLayout from "../common/components/MainLayout"
import { SetStateAction, useState } from "react"
import {
    Button,
    Upload,
    message,
    Modal,
    Space,
    Card,
    Typography,
    Alert,
    Descriptions,
    Tag,
    DatePicker
} from "antd"
import {
    MailOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InboxOutlined,
    DeleteOutlined,
    CalendarOutlined
} from "@ant-design/icons"
import useNotificacionesAPI from "./UseNotificacionesAPI"
import moment from "moment-business-days"
import locale from "antd/lib/date-picker/locale/es_ES"
import { set } from "lodash"

const { Title, Text, Paragraph } = Typography
const { Dragger } = Upload

export default () => {
    const [file, setFile] = useState<File | null>(null) // Estado directo para el archivo
    const [showModal, setShowModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const [fileInfo, setFileInfo] = useState({ name: '', size: '', type: '', lastModified: '' }) // Estado para la información del archivo
    const api = useNotificacionesAPI();
    const [fechaEnvio, setFechaEnvio] = useState(moment());


    // Configuración del upload para un solo archivo
    const uploadProps = {
        name: 'file',
        multiple: false, // Solo un archivo
        accept: '.csv,.txt',
        showUploadList: false, // Ocultamos la lista por defecto porque manejamos el estado manualmente
        beforeUpload: (file: File) => {
            const isValidType = file.name.endsWith('.csv') ||
                file.name.endsWith('.txt')

            if (!isValidType) {
                message.error('Solo se permiten archivos CSV o TXT')
                return false
            }

            const isLt10M = file.size / 1024 / 1024 < 10
            if (!isLt10M) {
                message.error('El archivo debe ser menor a 10MB')
                return false
            }

            // Guardar el archivo directamente
            setFile(file)
            setFileInfo({
                name: file.name,
                size: (file.size / 1024).toFixed(2),
                type: file.name.split('.').pop()?.toUpperCase() || '',
                lastModified: new Date(file.lastModified).toLocaleString()
            })

            message.success(`Archivo "${file.name}" seleccionado correctamente`)
            return false // Prevenir subida automática
        }
    }

    // Eliminar archivo seleccionado
    const removeFile = () => {
        setFile(null)
        setFileInfo({ name: '', size: '', type: '', lastModified: '' })
        message.info('Archivo removido')
    }

    // Enviar archivo al endpoint
    const sendFileByEmail = async () => {
        if (!file) {
            message.warning('Por favor, selecciona un archivo primero')
            return
        }

        setLoading(true)
        const formData = new FormData()
        formData.append("archivo", file)
        formData.append("fileName", file.name)
        formData.append("fechaEnvio", fechaEnvio ? fechaEnvio.format('YYYY-MM-DD') : '')

        try {
            // Reemplaza esta URL con la de tu endpoint
            var response = await api.MarcarEnviadasPostal(formData);

            if (!response.value) {
                throw new Error(response.data || "Error al enviar el archivo")
            }

            message.success({
                content: 'Archivo enviado exitosamente por email',
                icon: <CheckCircleOutlined />,
                duration: 3
            })

            // Limpiar después del envío
            setFile(null)
            setFileInfo({ name: '', size: '', type: '', lastModified: '' })
            setShowModal(false)
            handleDateChange(null) // Reiniciar fecha al valor por defecto (hoy)
        } catch (err: any) {
            message.error({
                content: err.message || "Error al conectar con el servidor",
                icon: <CloseCircleOutlined />,
                duration: 4
            })
        } finally {
            setLoading(false)
        }
    }

    // Mostrar modal de confirmación
    const showConfirmModal = () => {
        if (!file) {
            message.warning('Por favor, selecciona un archivo primero')
            return
        }
        setShowModal(true)
    }

    const handleDateChange = (date: any) => {
        console.log("Fecha seleccionada:", date)
        setFechaEnvio(date);
    };

    return (
        <MainLayout title="Servicio de notificaciones por correo POSTAL">
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px' }}>
                <Card
                    headStyle={{ backgroundColor: 'lightyellow' }}
                    title={
                        <Space>
                            <MailOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                Marcar actas como enviadas por correo POSTAL
                            </Title>
                        </Space>
                    }
                    bordered={false}
                    style={{
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderRadius: 8
                    }}
                >
                    {/* Campo de fecha de envío */}
                    <div style={{ marginBottom: 24 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>
                                <CalendarOutlined style={{ marginRight: 8 }} />
                                Fecha de envío postal:
                            </Text>
                            <DatePicker
                            locale={locale}
                                key={'fechaEnvio'}
                                format="DD/MM/YYYY"
                                placeholder="Selecciona la fecha de envío"
                                onChange={(value) => handleDateChange(value)}
                                value={fechaEnvio}
                                style={{ width: '100%' }}
                                size="large"
                                disabledDate={(current) => {
                                    // No permitir fechas futuras
                                    return current && current > moment().endOf('day');
                                }}
                            />
                        </Space>
                    </div>

                    {/* Área de drag and drop - SOLO SE MUESTRA SI NO HAY ARCHIVO */}
                    {!file && (
                        <Dragger {...uploadProps} style={{ marginBottom: 24 }}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Haz clic o arrastra un archivo aquí
                            </p>
                            <p className="ant-upload-hint">
                                Soporta archivos CSV o TXT. Tamaño máximo: 10MB
                            </p>
                        </Dragger>
                    )}

                    {/* Información del archivo seleccionado - SE MUESTRA CUANDO HAY ARCHIVO */}
                    {fileInfo && (
                        <Card
                            size="small"
                            style={{
                                marginBottom: 24,
                                backgroundColor: '#f0f5ff',
                                borderColor: '#1890ff'
                            }}
                            actions={[
                                <Button
                                    type="link"
                                    icon={<DeleteOutlined />}
                                    onClick={removeFile}
                                    danger
                                >
                                    Eliminar archivo
                                </Button>
                            ]}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text strong>
                                    <FileTextOutlined /> Archivo seleccionado:
                                </Text>
                                <Descriptions size="small" column={2}>
                                    <Descriptions.Item label="Nombre">
                                        <Tag color="blue">{fileInfo.name}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tamaño">
                                        {fileInfo.size} KB
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tipo">
                                        <Tag color="green">{fileInfo.type}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Fecha modificación">
                                        {fileInfo.lastModified}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Space>
                        </Card>
                    )}

                    {/* Botón de envío */}
                    <Button
                        type="primary"
                        size="large"
                        icon={<MailOutlined />}
                        onClick={showConfirmModal}
                        disabled={!file || loading}
                        loading={loading}
                        block
                        style={{ height: 48, fontSize: 16 }}
                    >
                        {loading ? 'Enviando...' : 'Generar envio por email'}
                    </Button>

                    {/* Alertas informativas */}
                    <Alert
                        message="Información importante"
                        description="Las actas serán enviadas al correo de cada titular, hasta 3 del mismo infractor. Cada acta será marcada como enviada por email."
                        type="warning"
                        showIcon
                        style={{ marginTop: 24 }}
                    />
                </Card>

                {/* Card con instrucciones */}
                <Card style={{ marginTop: 24, backgroundColor: '#fafafa' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>Instrucciones:</Text>
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                            <li>- Selecciona un archivo con extensión .csv o .txt</li>
                            <li>- El tamaño máximo permitido es de 10MB</li>
                            <li>- Solo se puede seleccionar un archivo a la vez</li>
                            <li>- Una vez seleccionado, haz clic en "Enviar por email"</li>
                            <li>- Confirma el envío en el modal que aparecerá</li>
                            <li>- Puedes eliminar el archivo seleccionado si cambias de opinión</li>
                        </ul>
                    </Space>
                </Card>
            </div>

            {/* Modal de confirmación */}
            <Modal
                title={
                    <Space>
                        <MailOutlined style={{ color: '#faad14' }} />
                        <span>Confirmar envío de archivo</span>
                    </Space>
                }
                open={showModal}
                onOk={sendFileByEmail}
                onCancel={() => setShowModal(false)}
                confirmLoading={loading}
                okText="Enviar"
                cancelText="Cancelar"
                width={500}
            >
                {fileInfo && (
                    <div style={{ padding: '16px 0' }}>
                        <Paragraph>
                            ¿Estás seguro de que deseas enviar el siguiente archivo por correo electrónico?
                        </Paragraph>
                        <Card size="small" style={{ backgroundColor: '#f5f5f5', marginTop: 16 }}>
                            <Space direction="vertical">
                                <Text strong>
                                    <FileTextOutlined /> {fileInfo.name}
                                </Text>
                                <Text type="secondary">
                                    Tamaño: {fileInfo.size} KB | Tipo: {fileInfo.type}
                                </Text>
                            </Space>
                        </Card>
                         {!fechaEnvio && <Alert
                            message="Cuidado"
                            description="No puso fecha de envío. Las actas se marcarán como enviadas con la fecha actual."
                            type="error"
                            showIcon
                            style={{ marginTop: 16 }}
                        />}
                        <Alert
                            message="Nota"
                            description="Esta acción no se puede deshacer. Asegúrate de que el archivo sea correcto."
                            type="warning"
                            showIcon
                            style={{ marginTop: 16 }}
                        />
                    </div>
                )}
            </Modal>
        </MainLayout>
    )
}

